import { createSelector } from 'reselect'
import {
  getCartType,
  getSelectedOutboundDepartingLocation,
  getSelectedOutboundArrivalLocation,
  getSelectedOutboundDepartureDateTime,
  getSelectedOutboundArrivalDateTime,
  getSelectedOutboundLogo,
  getSelectedOutboundHasMultipleCarriers,
  getSelectedOutboundOperatorName,
  getSelectedOutboundStopCount,
  getSelectedOutboundDuration,
  getSelectedOutboundTimeline,
  getSelectedInboundDepartingLocation,
  getSelectedInboundArrivalLocation,
  getSelectedInboundDepartureDateTime,
  getSelectedInboundArrivalDateTime,
  getSelectedInboundLogo,
  getSelectedInboundHasMultipleCarriers,
  getSelectedInboundOperatorName,
  getSelectedInboundStopCount,
  getSelectedInboundDuration,
  getSelectedInboundTimeline,
  getSelectedOutboundMinPrice,
  getSelectedOutboundHasFareOptions,
  getSelectedOutboundFareExtraCost,
  getSelectedOutboundFareNames,
  getSelectedOutboundFareRules,
  getSelectedOutboundFareRulesSummary,
  getSelectedInboundMinPrice,
  getSelectedInboundHasFareOptions,
  getSelectedInboundFareExtraCost,
  getSelectedInboundFareNames,
  getSelectedInboundFareRules,
  getSelectedInboundFareRulesSummary,
  getSelectedOutboundOtherDeals,
  getSelectedInboundOtherDeals,
  getNeedsDetailsLookup,
  getDetailsLinkForSelections,
  getSelectedOutboundBestDeal,
  getSelectedInboundBestDeal,
  getActiveModeHasNoResults,
} from './selectorsModel'
import { getSearchPaxCount } from 'tickets/redux/modules/searchParams/selectors'
import { getCurrencyCode } from 'tickets/redux/modules/user/selectors'
import { getCurrentModeId } from 'tickets/redux/modules/router/selectors'
import { getIsActiveModePolling } from 'tickets/redux/modules/modesInfo/selectors'

const getOutboundCartJourney = createSelector(
  [
    getCartType,
    getCurrentModeId,
    getSelectedOutboundDepartingLocation,
    getSelectedOutboundArrivalLocation,
    getSelectedOutboundDepartureDateTime,
    getSelectedOutboundArrivalDateTime,
    getSelectedOutboundLogo,
    getSelectedOutboundHasMultipleCarriers,
    getSelectedOutboundOperatorName,
    getSelectedOutboundStopCount,
    getSelectedOutboundDuration,
    getSelectedOutboundTimeline,
  ],
  (
    type,
    modeId,
    selectedOutboundDepartingLocation,
    selectedOutboundArrivalLocation,
    selectedOutboundDepartureDateTime,
    selectedOutboundArrivalDateTime,
    selectedOutboundLogo,
    selectedOutboundHasMultipleCarriers,
    selectedOutboundOperatorName,
    selectedOutboundStopCount,
    selectedOutboundDuration,
    selectedOutboundTimeline,
  ) => ({
    isOutbound: true,
    type,
    modeId,
    from: selectedOutboundDepartingLocation,
    to: selectedOutboundArrivalLocation,
    departureDateTime: selectedOutboundDepartureDateTime,
    arrivalDateTime: selectedOutboundArrivalDateTime,
    logo: selectedOutboundLogo,
    hasMultipleCarriers: selectedOutboundHasMultipleCarriers,
    operatorName: selectedOutboundOperatorName,
    stops: selectedOutboundStopCount,
    duration: selectedOutboundDuration,
    timeline: selectedOutboundTimeline,
  })
)

const getInboundCartJourney = createSelector(
  [
    getCartType,
    getCurrentModeId,
    getSelectedInboundDepartingLocation,
    getSelectedInboundArrivalLocation,
    getSelectedInboundDepartureDateTime,
    getSelectedInboundArrivalDateTime,
    getSelectedInboundLogo,
    getSelectedInboundHasMultipleCarriers,
    getSelectedInboundOperatorName,
    getSelectedInboundStopCount,
    getSelectedInboundDuration,
    getSelectedInboundTimeline,
  ],
  (
    type,
    modeId,
    selectedInboundDepartingLocation,
    selectedInboundArrivalLocation,
    selectedInboundDepartureDateTime,
    selectedInboundArrivalDateTime,
    selectedInboundLogo,
    selectedInboundHasMultipleCarriers,
    selectedInboundOperatorName,
    selectedInboundStopCount,
    selectedInboundDuration,
    selectedInboundTimeline,
  ) => ({
    isOutbound: false,
    type,
    modeId,
    from: selectedInboundDepartingLocation,
    to: selectedInboundArrivalLocation,
    departureDateTime: selectedInboundDepartureDateTime,
    arrivalDateTime: selectedInboundArrivalDateTime,
    logo: selectedInboundLogo,
    hasMultipleCarriers: selectedInboundHasMultipleCarriers,
    operatorName: selectedInboundOperatorName,
    stops: selectedInboundStopCount,
    duration: selectedInboundDuration,
    timeline: selectedInboundTimeline,
  })
)


const getOutboundCartFare = createSelector(
  [
    getSearchPaxCount,
    getCurrencyCode,
    getSelectedOutboundMinPrice,
    getSelectedOutboundHasFareOptions,
    getSelectedOutboundFareExtraCost,
    getSelectedOutboundFareNames,
    getSelectedOutboundFareRules,
    getSelectedOutboundFareRulesSummary,
  ],
  (
    passengerCount,
    currencyCode,
    selectedOutboundMinPrice,
    selectedOutboundHasFareOptions,
    selectedOutboundFareExtraCost,
    selectedOutboundFareNames,
    selectedOutboundFareRules,
    selectedOutboundFareRulesSummary,
  ) => ({
    isOutbound: true,
    passengerCount,
    currencyCode,
    price: selectedOutboundMinPrice,
    canUpgrade: selectedOutboundHasFareOptions,
    upgradeCost: selectedOutboundFareExtraCost,
    names: selectedOutboundFareNames,
    fareRules: {
      summaries: selectedOutboundFareRulesSummary,
      rules: selectedOutboundFareRules,
    },
  })
)

const getInboundCartFare = createSelector(
  [
    getSearchPaxCount,
    getCurrencyCode,
    getSelectedInboundMinPrice,
    getSelectedInboundHasFareOptions,
    getSelectedInboundFareExtraCost,
    getSelectedInboundFareNames,
    getSelectedInboundFareRules,
    getSelectedInboundFareRulesSummary,
  ],
  (
    passengerCount,
    currencyCode,
    selectedInboundMinPrice,
    selectedInboundHasFareOptions,
    selectedInboundFareExtraCost,
    selectedInboundFareNames,
    selectedInboundFareRules,
    selectedInboundFareRulesSummary,
  ) => ({
    isOutbound: false,
    passengerCount,
    currencyCode,
    price: selectedInboundMinPrice,
    canUpgrade: selectedInboundHasFareOptions,
    upgradeCost: selectedInboundFareExtraCost,
    names: selectedInboundFareNames,
    fareRules: {
      summaries: selectedInboundFareRulesSummary,
      rules: selectedInboundFareRules,
    },

  })
)

const getCartBook = createSelector(
  [
    getCartType,
    getCurrentModeId,
    getCurrencyCode,
    getNeedsDetailsLookup,
    getDetailsLinkForSelections,
  ],
  (
    cartType,
    currentModeId,
    currencyCode,
    needsDetailsLookup,
    detailsLinkForSelections,
  ) => ({
    type: cartType,
    modeId: currentModeId,
    currencyCode,
    needsToFetchFinalPrices: needsDetailsLookup,
    detailsLink: detailsLinkForSelections,
  })
)

const getOutboundCartBook = createSelector(
  [
    getCartBook,
    getSelectedOutboundBestDeal,
  ],
  (
    cartBook,
    selectedOutboundBestDeal
  ) => ({
    ...cartBook,
    ...selectedOutboundBestDeal,
  })
)

const getInboundCartBook = createSelector(
  [
    getCartBook,
    getSelectedInboundBestDeal,
  ],
  (
    cartBook,
    selectedOutboundBestDeal
  ) => ({
    ...cartBook,
    ...selectedOutboundBestDeal,
  })
)

const getOutboundOtherDeals = createSelector(
  [getCurrencyCode, getSelectedOutboundOtherDeals, getIsActiveModePolling],
  (currencyCode, selectedOutboundOtherDeals, isPolling) => ({
    currencyCode,
    deals: selectedOutboundOtherDeals,
    isPolling,
  })
)

const getInboundOtherDeals = createSelector(
  [getCurrencyCode, getSelectedInboundOtherDeals, getIsActiveModePolling],
  (currencyCode, selectedInboundOtherDeals, isPolling) => ({
    currencyCode,
    deals: selectedInboundOtherDeals,
    isPolling,
  })
)

// This is to have no padding on right column
const getHasHeader = createSelector(
  [getActiveModeHasNoResults, getCurrentModeId],
  (activeModeHasNoResults, currentModeId) =>
  (activeModeHasNoResults || currentModeId === 'airport-transfer')
)

export {
  getOutboundCartFare,
  getInboundCartFare,
  getOutboundCartJourney,
  getInboundCartJourney,
  getOutboundCartBook,
  getInboundCartBook,
  getOutboundOtherDeals,
  getInboundOtherDeals,
  getHasHeader,
}
